import React from "react";
import { TextField, TextFieldProps, InputAdornment } from "@mui/material";
import { styled } from "@mui/material/styles";

export interface InputFieldProps extends Omit<TextFieldProps, "variant"> {
  icon?: React.ReactNode;
  iconPosition?: "start" | "end";
  /** Passé dans InputProps.startAdornment (prioritaire sur icon si les deux sont fournis) */
  startAdornment?: React.ReactNode;
  /** Passé dans InputProps.endAdornment (prioritaire sur icon si les deux sont fournis) */
  endAdornment?: React.ReactNode;
}

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.shape.borderRadius,
    backgroundColor:
      theme.palette.mode === "light"
        ? theme.palette.background.default
        : "#1e1610",
    fontSize: "16px",
    height: "48px", // h-12
    transition: "all 200ms ease",
    "& fieldset": {
      borderColor: theme.palette.mode === "light" ? "#e8dbce" : "#4a3b2f", // border-light / border-dark
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused": {
      backgroundColor: theme.palette.background.paper,
      "& fieldset": {
        borderWidth: "2px",
        borderColor: theme.palette.primary.main,
      },
    },
    "&.Mui-error fieldset": {
      borderColor: theme.palette.error.main,
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "14px",
    fontWeight: 500,
    "&.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "12px 16px",
    "&::placeholder": {
      color: theme.palette.mode === "light" ? "#9c7349" : "#9ca3af",
      opacity: 0.7,
    },
  },
}));

/**
 * Composant InputField réutilisable avec support d'icônes
 *
 * @example
 * ```tsx
 * <InputField
 *   label="Email"
 *   type="email"
 *   icon={<EmailIcon />}
 *   iconPosition="start"
 * />
 *
 * <InputField
 *   label="Montant"
 *   icon={<EuroIcon />}
 *   iconPosition="end"
 *   error={hasError}
 *   helperText={errorMessage}
 * />
 * ```
 */
export const InputField: React.FC<InputFieldProps> = ({
  icon,
  iconPosition = "start",
  startAdornment: startAdornmentProp,
  endAdornment: endAdornmentProp,
  InputProps,
  ...props
}) => {
  const startAdornment =
    startAdornmentProp ??
    (iconPosition === "start" && icon ? (
      <InputAdornment position="start" sx={{ pl: 1 }}>
        {icon}
      </InputAdornment>
    ) : undefined);
  const endAdornment =
    endAdornmentProp ??
    (iconPosition === "end" && icon ? (
      <InputAdornment position="end" sx={{ pr: 1 }}>
        {icon}
      </InputAdornment>
    ) : undefined);

  return (
    <StyledTextField
      variant="outlined"
      InputProps={{
        ...InputProps,
        ...(startAdornment ? { startAdornment } : {}),
        ...(endAdornment ? { endAdornment } : {}),
      }}
      {...props}
    />
  );
};
